import { useCallback, useEffect, useState } from 'react'
import { api, setToken, type Account } from './api'
import { api as adminApi, setToken as setAdminToken } from './adminApi'
import { Landing } from './views/Landing'
import { Docs } from './views/Docs'
import { Auth } from './views/Auth'
import { Dashboard } from './views/Dashboard'
import { AdminLogin } from './views/AdminLogin'
import { AdminPanel } from './views/AdminPanel'

type Route = 'landing' | 'auth' | 'console' | 'admin' | 'docs'

function routeFromHash(): Route {
  const h = window.location.hash.replace(/^#\/?/, '').split('?')[0]
  if (h === 'auth' || h === 'login' || h === 'register') return 'auth'
  if (h === 'console' || h === 'dashboard' || h === 'keys') return 'console'
  if (h === 'admin' || h === 'tunnels') return 'admin'
  if (h === 'docs') return 'docs'
  return 'landing'
}

export function App() {
  const [route, setRoute] = useState<Route>(routeFromHash())
  const [account, setAccount] = useState<Account | null>(null) // regular user (keys)
  const [adminAuthed, setAdminAuthed] = useState(false)        // admin (tunnels)
  const [booting, setBooting] = useState(true)

  const go = useCallback((r: Route) => { window.location.hash = r === 'landing' ? '/' : `/${r}` }, [])

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Restore both sessions (separate refresh cookies) on first load.
  useEffect(() => {
    let alive = true
    ;(async () => {
      const [userOk, adminOk] = await Promise.all([api.refresh(), adminApi.refresh()])
      if (userOk) { try { const { account } = await api.me(); if (alive) setAccount(account) } catch { /* ignore */ } }
      if (adminOk && alive) setAdminAuthed(true)
      if (alive) setBooting(false)
    })()
    return () => { alive = false }
  }, [])

  // Bounce regular users between auth and console based on their session.
  useEffect(() => {
    if (booting) return
    if (account && route === 'auth') go('console')
    if (!account && route === 'console') go('auth')
  }, [account, route, booting, go])

  const onUserLogout = useCallback(async () => {
    await api.logout().catch(() => {})
    setToken(null); setAccount(null); go('landing')
  }, [go])
  const onAdminLogout = useCallback(async () => {
    await adminApi.logout().catch(() => {})
    setAdminToken(null); setAdminAuthed(false); go('landing')
  }, [go])

  if (booting) return <div className="empty" style={{ paddingTop: 120 }}><span className="spin" /></div>

  const onAdminPage = route === 'admin'

  return (
    <>
      <header className="topbar">
        <div className="wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <a className="brand" href="#/" onClick={() => go('landing')}>
            <span className="dot" /> svoisozvon <span className="mono">/ api</span>
          </a>
          <nav>
            <a className="btn ghost" href="#/docs" onClick={() => go('docs')}>Документация</a>
            {onAdminPage && adminAuthed ? (
              <button className="btn sm" onClick={onAdminLogout}>Выйти</button>
            ) : !onAdminPage && account ? (
              <>
                <a className="btn ghost" href="#/console" onClick={() => go('console')}>Консоль</a>
                <button className="btn sm" onClick={onUserLogout}>Выйти</button>
              </>
            ) : !onAdminPage ? (
              <button className="btn primary sm" onClick={() => go('auth')}>Войти</button>
            ) : null}
          </nav>
        </div>
      </header>

      <main className="wrap">
        {route === 'landing' && <Landing onStart={() => go(account ? 'console' : 'auth')} onDocs={() => go('docs')} />}
        {route === 'docs' && <Docs />}
        {route === 'auth' && <Auth onAuthed={(acc) => { setAccount(acc); go('console') }} />}
        {route === 'console' && account && <Dashboard account={account} />}
        {route === 'admin' && (adminAuthed ? <AdminPanel /> : <AdminLogin onAuthed={() => setAdminAuthed(true)} />)}
      </main>

      <footer className="wrap footer">
        <span>© {new Date().getFullYear()} svoisozvon · api.podryvanov.ru</span>
        <span className="mono">боты · сообщения · вебхуки</span>
      </footer>
    </>
  )
}
